<template>
  <v-layout row justify-space-between class="pa-2">
    <v-flex xs3>
      <v-layout column>
        <v-flex class="mb-4">
          <v-layout row align-center>
            <h3 class="all-caps mr-2">Start Date:</h3>
            <v-flex grow>
              <EmployeeDateField
                fieldType="date"
                label=""
                mask="date-with-time"
                fieldName="start_date"
                :object="employee"
                icon="false"
              />
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex>
          <h3 class="all-caps">Promo Dates:</h3>
          <ul>
            <li v-for="promo in employee.promotions">
              {{ promo.date | moment("MM/DD/YYYY") }} | {{ promo.notes }}
            </li>
          </ul>

          <v-dialog v-model="newPromoDialog" max-width="290">
            <v-btn slot="activator">
              <v-icon left>add</v-icon>
              Promote
            </v-btn>
            <v-card>
              <v-card-title class="headline"
                >Promote {{ employee.display_name }}</v-card-title
              >
              <v-card-text>
                <v-text-field
                  label="Notes"
                  placeholder="Position Change, Pay Raise, etc."
                  v-model="newPromotion.notes"
                >
                </v-text-field>
                <v-layout row wrap>
                  <v-flex xs12 sm6 md4>
                    <v-menu
                      ref="promotionDateMenu"
                      v-model="promotionDateMenu"
                      :close-on-content-click="false"
                      :nudge-right="40"
                      :return-value.sync="promoDatePlaceholder"
                      lazy
                      transition="scale-transition"
                      offset-y
                      full-width
                      min-width="290px"
                    >
                      <template slot="activator" slot-scope="{ on }">
                        <v-text-field
                          :value="newPromotion.date"
                          label="Promotion Date"
                          prepend-icon=""
                          readonly
                          v-on="on"
                        ></v-text-field>
                      </template>
                      <v-date-picker
                        v-model="promoDatePlaceholder"
                        no-title
                        scrollable
                      >
                        <v-spacer></v-spacer>
                        <v-btn
                          flat
                          color="primary"
                          @click="promotionDateMenu = false"
                          >Cancel</v-btn
                        >
                        <v-btn
                          flat
                          color="primary"
                          @click="savePromoDate(promoDatePlaceholder)"
                          >OK</v-btn
                        >
                      </v-date-picker>
                    </v-menu>
                  </v-flex>
                </v-layout>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="grey" flat @click="newPromoDialog = false"
                  >Nevermind</v-btn
                >
                <v-btn
                  color="success"
                  small
                  :loading="promoApiCallPending"
                  @click="createPromo"
                >
                  Create
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
      </v-layout>
    </v-flex>

    <v-flex xs6 mx-4>
      <v-sheet color="grey lighten-3">
        <v-layout row pa-2>
          <v-flex>
            <EmployeeSelect
              label="Position"
              fieldName="primary_position_id"
              :object="employee"
              :items="remainingPositionsForEmployee(1)"
              itemText="name"
              itemValue="id"
            />

            <EmployeeSelect
              label="Secondary"
              fieldName="secondary_position_id"
              :object="employee"
              :items="remainingPositionsForEmployee(2)"
              itemText="name"
              itemValue="id"
            />

            <v-checkbox
              v-model="morePositionsToggle"
              label="Add Additional Position"
              v-show="!showMorePositions"
            >
            </v-checkbox>

            <div v-show="showMorePositions">
              <EmployeeSelect
                label="Third"
                fieldName="third_position_id"
                :object="employee"
                :items="remainingPositionsForEmployee(3)"
                itemText="name"
                itemValue="id"
              />

              <EmployeeSelect
                v-show="showFourthPosition"
                label="Fourth"
                fieldName="fourth_position_id"
                :object="employee"
                :items="remainingPositionsForEmployee(4)"
                itemText="name"
                itemValue="id"
              />

              <EmployeeSelect
                v-show="showFifthPosition"
                label="Fifth"
                fieldName="fifth_position_id"
                :object="employee"
                :items="remainingPositionsForEmployee(5)"
                itemText="name"
                itemValue="id"
              />
            </div>
          </v-flex>
        </v-layout>
      </v-sheet>

      <EmployeeCheckboxField
        fieldType="checkbox"
        label="Student"
        fieldName="student"
        :object="employee"
      />

      <div v-if="employee.position_names.includes('CVT')">
        <EmployeeCheckboxField
          fieldType="checkbox"
          label="New Grad"
          fieldName="new_grad"
          :object="employee"
        />
      </div>

      <div v-if="employee.position_names.includes('Assistant')">
        <EmployeeCheckboxField
          fieldType="checkbox"
          label="Advanced"
          fieldName="advanced_assistant"
          :object="employee"
        />
      </div>
    </v-flex>

    <v-flex xs3>
      <EmployeeSelect
        label="Manager"
        fieldName="manager_id"
        :object="employee"
        :items="managersForDetailedEmployee"
        itemText="name"
        itemValue="id"
      />

      <EmployeeField
        fieldType="text"
        label="Base Rate"
        fieldName="base_rate"
        :object="employee"
        prefix="$"
      />

      <EmployeeSelect
        label="Vacation Accrual Rate"
        fieldName="vacation_accrual_rate"
        :object="employee"
        :items="possibleAccrualRates"
      />
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import Info from "@/components/hr/info.vue";
import EmployeeField from "@/components/hr/employee-field.vue";
import EmployeeDateField from "@/components/hr/employee-date-field.vue";
import EmployeeCheckboxField from "@/components/hr/employee-checkbox-field.vue";
import EmployeeSelect from "@/components/hr/employee-select.vue";

export default {
  components: {
    Info,
    EmployeeField,
    EmployeeDateField,
    EmployeeCheckboxField,
    EmployeeSelect
  },
  props: ["employee"],
  created() {
    this.$store.dispatch(
      "employees/getManagersForDetailedEmployee",
      this.$route.params.employeeId
    );
    this.$store.dispatch(
      "employees/getEmployee",
      this.$route.params.employeeId
    );
  },
  computed: {
    possibleAccrualRates: function() {
      return this.$store.state.employees.possibleAccrualRates;
    },
    managersForDetailedEmployee: function() {
      // TEMP (#4): Lanie Van Noordt (user 312) is a BoH user, so she is not in
      // the manager list the API returns, yet VSS wants her selectable as an
      // employee's manager. manager_id is just an FK to users.id, so pointing an
      // employee at her is valid regardless of role. Surface her here until her
      // role/manager setup is handled properly, then remove this block.
      const managers = this.$store.state.employees.managersForDetailedEmployee;
      const withLanie = managers.some(m => m.id === 312)
        ? managers
        : managers.concat([{ id: 312, name: "Lanie Van Noordt" }]);
      return withLanie.concat([{ id: null, name: "None" }]);
    },
    primaryPositionsForDetailedEmployee: function() {
      return this.$store.state.employees.primaryPositionsForDetailedEmployee.concat(
        [{ id: null, name: "None" }]
      );
    },
    showMorePositions: function() {
      if (
        (this.employee.primary_position_id &&
          this.employee.secondary_position_id) ||
        this.employee.third_position_id ||
        this.employee.fourth_position_id ||
        this.employee.fifth_position_id
      ) {
        return true;
      } else {
        return this.morePositionsToggle;
      }
    },
    showFourthPosition: function() {
      if (
        this.employee.fourth_position_id ||
        this.employee.third_position_id ||
        this.employee.fifth_position_id
      ) {
        return true;
      }
      return false;
    },
    showFifthPosition: function() {
      if (
        this.employee.fifth_position_id ||
        this.employee.fourth_position_id ||
        (this.employee.fourth_position_id && this.employee.third_position_id)
      ) {
        return true;
      }
      return false;
    }
  },
  data: function() {
    return {
      morePositionsToggle: false,
      newPromoDialog: false,
      promotionDateMenu: false,
      promoApiCallPending: false,
      managerLoading: false,
      managerErrors: [],
      managerSuccesses: [],
      positionLoading: false,
      positionErrors: [],
      positionSuccesses: [],
      promoDatePlaceholder: new Date().toISOString().substr(0, 10),
      newPromotion: {
        notes: "",
        date: ""
      }
    };
  },
  methods: {
    positionsForDetailedEmployeeSans: function(positionIds) {
      return this.primaryPositionsForDetailedEmployee.filter(p => {
        if (p.name == "None") {
          return p;
        } else {
          return !positionIds.includes(p.id);
        }
      });
    },
    remainingPositionsForEmployee: function(positionInteger) {
      const positionsToExclude = [
        this.employee.primary_position_id,
        this.employee.secondary_position_id,
        this.employee.third_position_id,
        this.employee.fourth_position_id,
        this.employee.fifth_position_id
      ];
      const positionsIndex = positionInteger - 1;
      return this.primaryPositionsForDetailedEmployee.filter(p => {
        if (p.name == "None") {
          // allow the None value through
          return true;
        } else if (positionsToExclude[positionsIndex] == p.id) {
          // also allow into a given select box any value that is selected for this box
          return true;
        } else {
          return !positionsToExclude.includes(p.id);
        }
      });
    },
    createPromo: function() {
      this.loading = true;
      this.$store
        .dispatch("employees/createPromotion", {
          employee_id: this.employee.id,
          notes: this.newPromotion.notes,
          date: this.newPromotion.date
        })
        .then(res => {
          console.log("ok", res);
        })
        .catch(res => {
          console.log("error", res);
        })
        .finally(res => {
          this.loading = false;
          this.newPromoDialog = false;
        });
    },
    savePromoDate: function(value) {
      this.newPromotion.date = value;
      this.promotionDateMenu = false;
    },
    wipeSuccesses: function() {
      this.managerSuccesses = [];
      this.positionSuccesses = [];
    }
  }
};
</script>
